<section class="next-best-decision" *ngIf="!loading && !error && nextDecision?.decision as decision; else heroState">
  <div class="next-best-decision__header">
    <div>
      <p class="next-best-decision__eyebrow">{{ lang.translate('home.nextBestDecision.eyebrow') }}</p>
      <h1 class="next-best-decision__title">{{ lang.translate('home.nextBestDecision.title') }}</h1>
    </div>
    <div class="next-best-decision__badges">
      <span class="next-best-decision__state" [attr.data-tone]="getStateTone(decision.state)">
        {{ stateLabel }}
      </span>
      <span class="next-best-decision__urgency" [attr.data-tone]="actionPresentation.urgencyTone">
        {{ actionPresentation.urgencyLabel }}
      </span>
    </div>
  </div>

  <div class="next-best-decision__body">
    <div class="next-best-decision__copy">
      <h2 class="next-best-decision__decision-title">{{ formatDecisionTitle(decision.title) }}</h2>
      <p class="next-best-decision__reason">{{ decision.shortReason }}</p>

      <section class="next-best-decision__status-card" [attr.data-tone]="actionPresentation.statusTone">
        <p class="next-best-decision__status-label">{{ lang.translate('home.nextBestDecision.functionalStatus') }}</p>
        <p class="next-best-decision__status-copy">{{ lang.translate('home.nextBestDecision.recommendationCopy') }}</p>
        <h3 class="next-best-decision__status-title">{{ actionPresentation.summaryLabel }}</h3>
        <p class="next-best-decision__status-copy">{{ actionPresentation.summaryCopy }}</p>
        <p class="next-best-decision__status-note" *ngIf="actionDisabledReason">{{ actionDisabledReason }}</p>
      </section>

      <dl class="next-best-decision__facts">
        <div class="next-best-decision__fact" *ngIf="decision.dueAt">
          <dt>{{ lang.translate('home.nextBestDecision.dueAt') }}</dt>
          <dd>
            <span class="next-best-decision__fact-value">{{ decision.dueAt | date: 'mediumDate' }}</span>
          </dd>
        </div>

        <div class="next-best-decision__fact">
          <dt>{{ lang.translate('home.nextBestDecision.priority') }}</dt>
          <dd>
            <span class="next-best-decision__fact-value">{{ priorityDisplay.label }} · {{ priorityDisplay.scoreLabel }}</span>
            <span class="next-best-decision__fact-copy">{{ priorityDisplay.summary }}</span>
          </dd>
        </div>

        <div class="next-best-decision__fact">
          <dt>{{ lang.translate('home.nextBestDecision.risk') }}</dt>
          <dd>
            <span class="next-best-decision__fact-value">{{ riskDisplay.label }}</span>
            <span class="next-best-decision__fact-copy">{{ riskDisplay.summary }}</span>
          </dd>
        </div>

        <div class="next-best-decision__fact">
          <dt>{{ lang.translate('home.nextBestDecision.confidence') }}</dt>
          <dd>
            <span class="next-best-decision__fact-value">{{ confidenceDisplay.label }} · {{ confidenceLabel }}</span>
            <span class="next-best-decision__fact-copy">{{ confidenceDisplay.summary }}</span>
          </dd>
        </div>
      </dl>
    </div>

    <div class="next-best-decision__actions">
      <button
        *ngIf="showPrimaryAction"
        type="button"
        class="btn next-best-decision__cta"
        [class.btn-primary]="actionPresentation.primaryMode === 'execute'"
        [class.btn-outline-primary]="actionPresentation.primaryMode === 'inspect'"
        [class.btn-outline-secondary]="actionPresentation.primaryMode === 'disabled'"
        [attr.data-mode]="actionPresentation.primaryMode"
        [disabled]="isPrimaryActionDisabled"
        (click)="onExecutePrimaryAction()"
      >
        {{ actionPresentation.primaryLabel }}
      </button>

      <button
        *ngIf="showSecondaryAction"
        type="button"
        class="btn btn-outline-primary next-best-decision__secondary"
        [class.next-best-decision__secondary--selected]="isSelected"
        (click)="onSelectDecision()"
      >
        {{ lang.translate('home.nextBestDecision.viewDecision') }}
      </button>

      <div class="next-best-decision__feedback" *ngIf="actionMessage || actionError">
        <p class="next-best-decision__feedback-success" *ngIf="actionMessage">{{ actionMessage }}</p>
        <p class="next-best-decision__feedback-error" *ngIf="actionError as currentActionError">{{ currentActionError.message }}</p>
      </div>
    </div>
  </div>
</section>

<ng-template #heroState>
  <div class="next-best-decision next-best-decision--state" *ngIf="error as currentError; else heroNoError">
    <p class="next-best-decision__state-title">{{ currentError.title }}</p>
    <p class="next-best-decision__state-copy">{{ currentError.message }}</p>
    <button type="button" class="btn btn-outline-primary" (click)="onRetry()">
      {{ lang.translate('home.general.retry') }}
    </button>
  </div>

  <ng-template #heroNoError>
    <output class="next-best-decision next-best-decision--state" aria-live="polite" *ngIf="loading; else heroAllClear">
      <span class="next-best-decision__state-spinner" aria-hidden="true"></span>
      <p class="next-best-decision__state-copy">{{ lang.translate('home.general.loading') }}</p>
    </output>

    <ng-template #heroAllClear>
      <section
        class="next-best-decision next-best-decision--all-clear"
        [attr.aria-label]="lang.translate('home.nextBestDecision.allClear.title')"
      >
        <span class="next-best-decision__all-clear-icon" aria-hidden="true">
          <i class="fa fa-circle-check"></i>
        </span>
        <div class="next-best-decision__all-clear-copy">
          <h1 class="next-best-decision__all-clear-title">{{ lang.translate('home.nextBestDecision.allClear.title') }}</h1>
          <p class="next-best-decision__all-clear-subtitle">{{ lang.translate('home.nextBestDecision.allClear.subtitle') }}</p>
        </div>

        <nav
          class="next-best-decision__all-clear-links"
          [attr.aria-label]="lang.translate('home.nextBestDecision.allClear.navLabel')"
        >
          <ng-container *ngFor="let link of allClearLinks">
            <a
              *ngIf="link.available$ | async"
              class="next-best-decision__all-clear-link"
              [routerLink]="link.route"
              [attr.aria-label]="lang.translate(link.labelKey)"
            >
              <i class="fa {{ link.icon }}" aria-hidden="true"></i>
              <span>{{ lang.translate(link.labelKey) }}</span>
              <i class="fa fa-chevron-right next-best-decision__all-clear-link-chevron" aria-hidden="true"></i>
            </a>
          </ng-container>
        </nav>
      </section>
    </ng-template>
  </ng-template>
</ng-template>
